import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { setLoading } from "../../reducer/auth/auth";
import { createGift, getGiftOptions, previewGiftMediums, isOk, messageOf } from "../../services/gift";
import { RECAPTCHA_SITE_KEY } from "../../utils/recaptcha";
import { Check } from "./components/GiftShell";
import ValidTill, { dayFromToday } from "./components/ValidTill";
import "./gift.css";
import "./redeem.css";

// "Give a VerySoul Experience" — logged-in clients (shortcuts) and mediums
// (shortcut/overview page). Creates the gift and sends the purchaser to the
// $2 Stripe checkout; Stripe returns to /gift-created.
export default function GiveGift() {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <GiveGiftForm />
    </GoogleReCaptchaProvider>
  );
}

// Where "Back" returns to (see goBack).
const BACK_KEY = "vs_give_gift_back";

function GiveGiftForm() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [options, setOptions] = useState(null);
  const [level, setLevel] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAmount, setShowAmount] = useState(false);
  const [secretWord, setSecretWord] = useState("");
  // Valid Till (YYYY-MM-DD): a year from today unless the giver picks another day.
  const [validTill, setValidTill] = useState(null);
  // "What would my recipient be offered?" for the selected maximum.
  const [preview, setPreview] = useState({ loading: false, mediums: null, max: null });

  useEffect(() => {
    if (location.state && location.state.from) {
      try {
        sessionStorage.setItem(BACK_KEY, location.state.from);
      } catch (err) {
        // storage blocked: Back still works until the page reloads
      }
    }
    if (new URLSearchParams(location.search).get("cancelled")) {
      toast.info("Payment was cancelled. Your gift has not been created.");
    }
    dispatch(setLoading(true));
    getGiftOptions().then((res) => {
      dispatch(setLoading(false));
      if (isOk(res)) {
        setOptions(res.data.data);
        setValidTill(dayFromToday(res.data.data.expiry_days || 365));
        const levels = res.data.data.levels || [];
        setLevel(levels.length > 1 ? levels[1] : levels.length ? levels[0] : null);
      } else {
        toast.error(messageOf(res, "Gift options could not be loaded."));
      }
    });
  }, []);

  const maxPrice = level === "custom" ? Number(customAmount) : level;
  const fee = options ? Number(options.fee).toFixed(2) : "2.00";
  const currency = (options && options.currency) || "USD";

  // Reload the preview shortly after the maximum settles on a valid amount.
  const previewable = !!options && !!maxPrice && maxPrice >= options.custom_min && maxPrice <= options.custom_max;
  useEffect(() => {
    if (!previewable) {
      setPreview({ loading: false, mediums: null, max: null });
      return undefined;
    }
    let cancelled = false;
    setPreview((current) => ({ ...current, loading: true }));
    const timer = setTimeout(() => {
      previewGiftMediums(maxPrice).then((res) => {
        if (cancelled) return;
        setPreview({ loading: false, mediums: isOk(res) ? res.data.data : null, max: maxPrice });
      });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [maxPrice, previewable]);

  const submit = async (e) => {
    e.preventDefault();
    if (!options) return;
    if (level === "custom" && (!customAmount || maxPrice < options.custom_min || maxPrice > options.custom_max)) {
      toast.error(`Please enter an amount between ${options.custom_min} and ${options.custom_max}.`);
      return;
    }
    if (secretWord.trim().length < 3) {
      toast.error("Please choose a secret word of at least 3 characters (Step 3).");
      return;
    }
    if (!acknowledged) {
      toast.error("Please confirm that you understand the gift creation fee is non-refundable.");
      return;
    }

    setSubmitting(true);
    dispatch(setLoading(true));
    let recaptchaToken = "";
    try {
      recaptchaToken = executeRecaptcha ? await executeRecaptcha("gift_create") : "";
    } catch (err) {
      recaptchaToken = "";
    }

    const res = await createGift({
      max_price: maxPrice,
      show_amount: showAmount,
      expires_at: validTill,
      gift_word: secretWord.trim(),
      contact_channel: "email",
      fee_acknowledged: true,
      recaptcha_token: recaptchaToken,
    });
    dispatch(setLoading(false));
    setSubmitting(false);

    if (isOk(res) && res.data.data && res.data.data.approvedUrl) {
      window.open(res.data.data.approvedUrl, "_self");
    } else {
      toast.error(messageOf(res, "We could not start the payment. Please try again."));
    }
  };

  // "Back": the page that opened this one passes it as state.from (Shortcuts,
  // My Gifts, service entry...). It's kept for this tab so Back still works after
  // a cancelled Stripe checkout (a full reload, which drops router state).
  const goBack = () => {
    let from = location.state && location.state.from;
    try {
      from = from || sessionStorage.getItem(BACK_KEY);
    } catch (err) {
      // storage blocked: use the default below
    }
    navigate(from || "/shortcuts");
  };

  if (!options) {
    return <div className="vsr-page vsr-inner" />;
  }

  const customInvalid =
    level === "custom" && customAmount !== "" && (maxPrice < options.custom_min || maxPrice > options.custom_max);

  return (
    <div className="vsr-page vsr-inner">
      <div className="vsr-stack">
        <div>
          <button type="button" className="vsr-link vsr-back-link" onClick={goBack}>
            &larr; Back
          </button>
        </div>
        <section className="vsr-card vsr-give-hero">
          <div className="vsr-give-hero-text">
            <p className="vsr-eyebrow vsr-eyebrow-left">VerySoul Gift</p>
            <h1 className="vsr-title vsr-title-left">Give a VerySoul Experience</h1>
            <p className="vsr-give-tagline">A different kind of gift.</p>
            <p className="vsr-lead vsr-lead-left">
              Give someone you care about the opportunity to experience a private session with a VerySoul Verified Professional Medium —
              while letting them choose the medium who feels right for them.
            </p>
            <Link className="vsr-link" to="/my-gifts">
              View the gifts you've already created &rarr;
            </Link>
            <p className="vsr-give-hero-note">
              Unlike typical retail gift cards, you don't fund the full amount up front. You pay a ${fee} creation fee now, and you
              approve and pay the rest when and if your gift is redeemed.
            </p>
          </div>
          <ul className="vsr-give-points">
            <li>
              <span className="vsr-give-point-icon" aria-hidden="true">
                <Check />
              </span>
              <div>
                <b>Purchase the gift card now and the session later.</b>
                <span>
                  Create your electronic gift card for {currency} ${fee} to print or send.
                </span>
              </div>
            </li>
            <li>
              <span className="vsr-give-point-icon" aria-hidden="true">
                <Check />
              </span>
              <div>
                <b>They choose their medium and time.</b>
                <span>We only offer sessions that fit within your budget. You pay when they book.</span>
              </div>
            </li>
            <li>
              <span className="vsr-give-point-icon" aria-hidden="true">
                <Check />
              </span>
              <div>
                <b>If they never use it, you never pay the session fee.</b>
                <span>No prepaid balance. No wasted funds. The potential gift remains open unless you choose to cancel.</span>
              </div>
            </li>
          </ul>
        </section>

        <form className="vsr-give" onSubmit={submit} noValidate>
          <div className="vsr-give-main">
            <section className="vsr-card vsr-give-step">
              <p className="vsr-give-step-num">Step 1</p>
              <h2 className="vsr-give-step-title">The most you'd like to cover for one session</h2>
              <p className="vsr-give-step-tagline">See what your gift can buy.</p>
              <p className="vsr-muted">
                This is a maximum session price, not a balance. If your recipient chooses a less expensive session, you'll only pay that
                session's price.
              </p>
              <div className="vsr-choices" role="radiogroup" aria-label="Maximum session price">
                {options.levels.map((value) => (
                  <label key={value} className={`vsr-choice ${level === value ? "is-selected" : ""}`}>
                    <input type="radio" name="level" checked={level === value} onChange={() => setLevel(value)} />
                    <span className="vsr-choice-small">Up to</span>
                    <span className="vsr-choice-amount">
                      {currency} {value}
                    </span>
                  </label>
                ))}
                <label className={`vsr-choice ${level === "custom" ? "is-selected" : ""}`}>
                  <input type="radio" name="level" checked={level === "custom"} onChange={() => setLevel("custom")} />
                  <span className="vsr-choice-small">Choose</span>
                  <span className="vsr-choice-amount">Custom</span>
                </label>
              </div>
              {level === "custom" && (
                <div className="vsr-field vsr-custom-amount">
                  <label className="vsr-label" htmlFor="gift-custom-amount">
                    Custom maximum ({currency})
                  </label>
                  <input
                    id="gift-custom-amount"
                    className={customInvalid ? "vsr-input is-invalid" : "vsr-input"}
                    type="number"
                    inputMode="decimal"
                    min={options.custom_min}
                    max={options.custom_max}
                    placeholder={`${options.custom_min} – ${options.custom_max}`}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                  />
                  <span className={customInvalid ? "vsr-error" : "vsr-hint"}>
                    Between {currency} {options.custom_min} and {currency} {options.custom_max}.
                  </span>
                </div>
              )}

              <label className="vsr-check vsr-give-show-amount">
                <input type="checkbox" checked={showAmount} onChange={(e) => setShowAmount(e.target.checked)} />
                <span>
                  Let my recipient see the value of the gift
                  {maxPrice ? (
                    <>
                      {" "}
                      — "worth up to <b>{currency} {maxPrice}</b>"
                    </>
                  ) : null}
                  . Leave unchecked to keep the amount private.
                </span>
              </label>

              {previewable && (
                <div className="vsr-give-preview" aria-live="polite">
                  <h3 className="vsr-give-preview-title">Mediums available for this amount</h3>
                  {preview.loading && !preview.mediums && <p className="vsr-muted">Loading…</p>}
                  {preview.mediums && preview.mediums.count === 0 && (
                    <p className="vsr-muted">
                      No mediums offer a session at or under {currency} {maxPrice} right now. Try a higher maximum.
                    </p>
                  )}
                  {preview.mediums && preview.mediums.count > 0 && (
                    <>
                      <p className="vsr-give-preview-count">
                        <b>{preview.mediums.count}</b> {preview.mediums.count === 1 ? "medium is" : "mediums are"} available at or under {currency}{" "}
                        {preview.max}
                      </p>
                      <p className="vsr-muted">
                        Your recipient picks the one who feels right for them; you only pay for the session they choose.
                      </p>
                      <ul className="vsr-give-preview-prices">
                        {preview.mediums.prices.slice(0, 8).map((point) => (
                          <li key={point.currency + point.amount}>
                            <span>
                              {point.currency} {Number(point.amount).toFixed(2)}
                              {point.currency !== preview.mediums.currency && (
                                <span className="vsr-muted">
                                  {" "}
                                  (about {preview.mediums.currency} {Number(point.approx).toFixed(2)})
                                </span>
                              )}
                            </span>
                            <b>
                              {point.mediums} {point.mediums === 1 ? "medium" : "mediums"}
                            </b>
                          </li>
                        ))}
                      </ul>
                      {preview.mediums.prices.length > 8 && (
                        <p className="vsr-hint">and {preview.mediums.prices.length - 8} more price points, up to your maximum.</p>
                      )}
                      {preview.mediums.prices.some((point) => point.currency !== preview.mediums.currency) && (
                        <p className="vsr-hint">
                          Some mediums price in another currency. They are shown in their own currency, and compared with your maximum at
                          today's exchange rate.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </section>

            <section className="vsr-card vsr-give-step">
              <p className="vsr-give-step-num">Step 2</p>
              <h2 className="vsr-give-step-title">How we'll contact you if your gift is used</h2>
              <p className="vsr-give-contact">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <polyline points="22 6 12 13 2 6" />
                </svg>
                <span>
                  We'll email you at <b>{options.purchaser && options.purchaser.email}</b>
                </span>
              </p>
              <p className="vsr-muted">
                This is how we'll contact you if someone uses your gift. Be suspicious of payment requests that arrive another way.
              </p>
            </section>

            <section className="vsr-card vsr-give-step">
              <p className="vsr-give-step-num">Step 3</p>
              <h2 className="vsr-give-step-title">Choose a secret word</h2>
              <p className="vsr-muted">
                Pick a word or phrase only you would choose. When someone uses your gift, VerySoul's payment request email will show it, so
                you can be sure the request is really from us. If it's missing, don't pay.
              </p>
              <div className="vsr-field">
                <label className="vsr-label" htmlFor="gift-secret-word">
                  Your secret word
                </label>
                <input
                  id="gift-secret-word"
                  className="vsr-input"
                  type="text"
                  maxLength={60}
                  autoComplete="off"
                  value={secretWord}
                  onChange={(e) => setSecretWord(e.target.value)}
                  placeholder="3–60 characters"
                />
                <span className="vsr-hint">Don't share it with your recipient. You can change it later from My Gifts.</span>
              </div>
            </section>

            <section className="vsr-give-trust">
              <svg className="vsr-give-trust-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6l8-3z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
              <div>
                <h2>How you'll know it's really us</h2>
                <p>
                  Unfortunately, payment scams and impersonation occur in the mediumship world, so we've built safeguards into VerySoul Gifts
                  from the beginning. If your gift is used:
                </p>
                <ul>
                  <li>We'll contact you only by email, at the address above.</li>
                  <li>
                    We'll show you the email address given by the person actually requesting the session — information we aren't asking you
                    to give us now.
                  </li>
                </ul>
                <p>
                  If any of those things don't look right, <b>don't pay</b>. Come directly to VerySoul rather than responding to the request.
                </p>
              </div>
            </section>
          </div>

          <aside className="vsr-card vsr-give-summary">
            <p className="vsr-eyebrow vsr-eyebrow-left">Your gift</p>
            <dl className="vsr-give-summary-list">
              <dt>Covers a session up to</dt>
              <dd>{maxPrice ? `${currency} ${maxPrice}` : "—"}</dd>
              <dt>Amount shown to recipient</dt>
              <dd>{showAmount && maxPrice ? `"Worth up to ${currency} ${maxPrice}"` : "Not shown"}</dd>
              <dt>Valid Till</dt>
              <dd>
                {validTill ? (
                  <ValidTill
                    label=""
                    day={validTill}
                    maxDays={options.max_expiry_days}
                    onSave={(day) => {
                      setValidTill(day);
                      return true;
                    }}
                  />
                ) : (
                  "—"
                )}
              </dd>
              <dt>Session payment</dt>
              <dd>Only if it's used</dd>
              <dt className="vsr-give-summary-total">Gift Creation Fee today</dt>
              <dd className="vsr-give-summary-total">
                {currency} {fee}
              </dd>
            </dl>
            <p className="vsr-give-fee-note">
              This small, non-refundable fee creates your VerySoul Gift and unique Gift Code. It is not payment toward a mediumship session
              and does not create a prepaid balance.
            </p>
            <label className="vsr-check vsr-give-ack">
              <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
              <span>
                I understand that I am authorizing a non-refundable {currency} {fee} VerySoul Gift Creation Fee.
              </span>
            </label>
            <button type="submit" className="vsr-btn vsr-btn-block" disabled={submitting}>
              {submitting ? "Starting checkout…" : `Create My Gift — ${currency} ${fee}`}
            </button>
            <p className="vsr-hint vsr-give-secure">Secure checkout by Stripe.</p>
          </aside>
        </form>
      </div>
    </div>
  );
}
